import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Group, Input, Stack, Switch, Text, TextInput } from '@mantine/core';

import { RootState } from 'store';
import { CatalogType } from 'store/catalogs/types';
import { userIsAdmin, userIsSuperAdmin } from 'store/user/selectors';
import { UserSelectorType } from 'store/user/types';
import { AdminAccess, UserSearch } from 'common';

type SetAttributesPropsType = {
  catalog: Partial<CatalogType>;
  onValueChange: (attributeKey: keyof CatalogType, newValue: string | number | boolean) => void;
};

const SetAttributes = ({ catalog, onValueChange }: SetAttributesPropsType) => {
  const { t } = useTranslation();

  const handleUserSelection = (user: UserSelectorType) => {
    const { user_id, first_name, last_name } = user;
    onValueChange('user_id', user_id);
    onValueChange('user_name', `${first_name} ${last_name}`);
  };

  const { currentUser, isAdmin } = useSelector((state: RootState) => ({
    currentUser: state.user.currentUser,
    isAdmin: userIsAdmin(state) || userIsSuperAdmin(state),
  }));

  const canShareCatalog =
    isAdmin || [catalog.user_id, catalog.created_by_id].includes(currentUser?.id);

  return (
    <Stack mr="xl" mb="xl">
      <Group justify="space-between" align="flex-start" gap="xl">
        <Group justify="flex-start" align="flex-start" gap="xl">
          <Input.Wrapper label={t('name')}>
            <TextInput
              onChange={(event) => onValueChange('name', event.currentTarget.value)}
              value={catalog.name || ''}
            />
          </Input.Wrapper>
          <AdminAccess>
            <Input.Wrapper label={t('user')}>
              <UserSearch
                placeholder={t('searchByUser')}
                onSelect={(user) => handleUserSelection(user)}
                defaultValue={catalog.user_name}
              />
            </Input.Wrapper>
          </AdminAccess>
          <Input.Wrapper label={t('createdBy')}>
            <Text>{catalog.created_by_name}</Text>
          </Input.Wrapper>
        </Group>
        {canShareCatalog && (
          <>
            <Input.Wrapper label={t('shareWithAgencies')}>
              <Switch
                checked={catalog.share_with_agencies}
                onChange={() => onValueChange('share_with_agencies', !catalog?.share_with_agencies)}
                mt="0.25rem"
              />
            </Input.Wrapper>
            <Input.Wrapper label={t('shareWithSubAgencies')}>
              <Switch
                checked={catalog.share_with_sub_agencies}
                onChange={() =>
                  onValueChange('share_with_sub_agencies', !catalog?.share_with_sub_agencies)
                }
                mt="0.25rem"
              />
            </Input.Wrapper>
          </>
        )}
        <Input.Wrapper label={t('active')}>
          <Switch
            checked={catalog.show_in_ui}
            onChange={() => onValueChange('show_in_ui', !catalog?.show_in_ui)}
            mt="0.25rem"
          />
        </Input.Wrapper>
      </Group>
    </Stack>
  );
};

export default SetAttributes;
